import React, { useContext, useEffect, useMemo } from 'react';
import { AuthContext } from 'contexts';
import styled from 'styled-components';
import { Button } from '../Button';
import errorReporter from '../../app/errorReporter';

const StyledEyeSvg = styled.svg`
  fill: #6a6c6f;
  position: relative;
  top: 3px;
`;

const PreviewButton = ({
  className,
  listing,
  listingId,
  hideStyling = false,
  isDisabled = false,
  angularInput,
  isBorderLess,
}: {
  listing: any;
  listingId?: string;
  className?: string;
  hideStyling?: boolean;
  isDisabled?: boolean;
  angularInput?: {
    $rootScope: any;
    ModalService: any;
  };
  isBorderLess?: boolean;
}) => {
  const authContext = useContext(AuthContext);
  const auth = authContext.auth || angularInput?.$rootScope.auth;

  const isAuthForLocation = useMemo(() => {
    if (!auth) return false;

    const listingLocations = Array.from(
      new Set([listing.location?.toString(), ...(listing.locations || [])]),
    ).filter(Boolean);

    const userAuthorizedLocations = Array.from(
      new Set([auth.location, ...(auth.locations || [])]),
    ).filter(Boolean);

    return listingLocations.some((location: string) =>
      userAuthorizedLocations.includes(location),
    );
  }, [auth, listing]);

  const $rootScope = useMemo(() => {
    if (angularInput) return angularInput.$rootScope;
    return angular.element(document).scope();
  }, [angularInput]);

  const ModalService = useMemo(() => {
    if (angularInput) return angularInput.ModalService;
    return angular.element(document).injector().get('ModalService');
  }, [angularInput]);

  useEffect(() => {
    if (!listing && !listingId) {
      errorReporter.error(
        'you must provide a listingId if there is no listing provided to the preview button',
      );
    }
  }, []);

  const openPreview = () => {
    if (isDisabled) return;

    $rootScope.$evalAsync(() => {
      ModalService.openPreviewModal(listing);
    });
  };

  if (!auth) return null;
  if (listing.isDeleted) return null;
  if (listing.org?.features?.includes('APPOINTMENT') === false) return null;
  if (
    listing.org?.toString() !== auth?.org?.id &&
    listing.org?.id?.toString() !== auth?.org?.id
  )
    return null;
  if (
    !auth ||
    !(
      auth.roles[auth.role] >= auth.roles.GLOBAL_ADMIN ||
      (auth.roles[auth.role] === auth.roles.BRANCH_ADMIN && isAuthForLocation)
    )
  )
    return null;

  const ButtonComponent = isBorderLess ? 'button' : Button;

  return (
    <>
      <ButtonComponent
        className={
          (!hideStyling
            ? `btn btn-default pull-right margin-right hidden-xs`
            : ``) +
          (isDisabled ? ` disabled` : ``) +
          (className ? ` ${className}` : ``)
        }
        onClick={openPreview}
        data-cy="eyeball-tool"
      >
        <StyledEyeSvg
          width="22"
          height="16"
          viewBox="0 0 22 16"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M20.9333 7.23946C18.7114 2.55899 15.3528 0.203522 10.8505 0.203522C6.34579 0.203522 2.98954 2.55899 0.767665 7.2418C0.678544 7.43051 0.632324 7.63662 0.632324 7.84532C0.632324 8.05402 0.678544 8.26012 0.767665 8.44883C2.98954 13.1293 6.34813 15.4848 10.8505 15.4848C15.3552 15.4848 18.7114 13.1293 20.9333 8.44649C21.1138 8.0668 21.1138 7.62618 20.9333 7.23946ZM10.8505 13.7973C7.07001 13.7973 4.30204 11.8801 2.3497 7.84415C4.30204 3.80821 7.07001 1.89102 10.8505 1.89102C14.6309 1.89102 17.3989 3.80821 19.3513 7.84415C17.4013 11.8801 14.6333 13.7973 10.8505 13.7973ZM10.7567 3.71915C8.4786 3.71915 6.63173 5.56602 6.63173 7.84415C6.63173 10.1223 8.4786 11.9691 10.7567 11.9691C13.0349 11.9691 14.8817 10.1223 14.8817 7.84415C14.8817 5.56602 13.0349 3.71915 10.7567 3.71915ZM10.7567 10.4691C9.30595 10.4691 8.13173 9.29493 8.13173 7.84415C8.13173 6.39337 9.30595 5.21915 10.7567 5.21915C12.2075 5.21915 13.3817 6.39337 13.3817 7.84415C13.3817 9.29493 12.2075 10.4691 10.7567 10.4691Z" />
        </StyledEyeSvg>
      </ButtonComponent>
    </>
  );
};

export default PreviewButton;
